import type React from 'react';
import { useMemo } from 'react';

import type { OrderParameter, SortUsersParameter } from '@vlabs/api/artemy/model';
import { ChevronSortIcon } from '@vlabs/icons';
import type { TOption } from '@vlabs/uikit/types';

import { DropdownSelect } from '@vlabs/monokit/shared/ui/dropdown-select';

import { useUserFiltersTranslation } from './i18n';
import { useUserListFilters } from './useUserListPageFilters';

interface TUserListSortOptionValue {
  sort: SortUsersParameter;
  order: OrderParameter;
}

const SORT_OPTIONS: TUserListSortOptionValue[] = [
  { sort: 'full_name', order: 'asc' },
  { sort: 'full_name', order: 'desc' },
  { sort: 'last_login_at', order: 'asc' },
  { sort: 'last_login_at', order: 'desc' },
  { sort: 'create_time', order: 'asc' },
  { sort: 'create_time', order: 'desc' },
  { sort: 'update_time', order: 'asc' },
  { sort: 'update_time', order: 'desc' },
];

function getSortOptionKey({ sort, order }: TUserListSortOptionValue): string {
  return `${sort}_${order}`;
}

function UserListSortDropdown(): React.JSX.Element {
  const { t } = useUserFiltersTranslation();
  const { filters, updateFilters } = useUserListFilters();

  const options: TOption<TUserListSortOptionValue>[] = useMemo(() => {
    return SORT_OPTIONS.map((value) => ({
      label: t(`sort.${getSortOptionKey(value)}`),
      value,
    }));
  }, [t]);

  const currentFiltersValue = { sort: filters.sort, order: filters.order } as TUserListSortOptionValue;

  const selectedOption = options.find((option) => {
    return getSortOptionKey(option.value) === getSortOptionKey(currentFiltersValue);
  });

  const onSelect = (nextValue: TUserListSortOptionValue): void => {
    updateFilters(nextValue);
  };

  return (
    <DropdownSelect<TUserListSortOptionValue>
      icon={<ChevronSortIcon />}
      label={selectedOption?.label as string}
      onSelect={onSelect}
      options={options}
      value={currentFiltersValue}
    />
  );
}

export { UserListSortDropdown };
